import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  Package, 
  Download,
  RefreshCw,
  AlertCircle,
  Target,
  ShoppingCart,
  Clock,
  BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  calculateMembershipMetrics,
  calculateUsageMetrics,
  calculateFinancialMetrics,
  exportAnalyticsData,
  clearAnalyticsCache,
  type MembershipMetrics,
  type UsageMetrics,
  type FinancialMetrics
} from '../services/rushdAnalyticsService';


// Helper function to convert period to date range
const getPeriodDateRange = (period: string): { startDate: Date; endDate: Date } => {
  const now = new Date();
  let startDate: Date;
  let endDate: Date = new Date(now);

  switch (period) {
    case '7days':
      startDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
      break;
    case '30days':
      startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      break;
    case '90days':
      startDate = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
      break;
    case 'current_month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'last_month':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of previous month
      break;
    case 'current_year':
      startDate = new Date(now.getFullYear(), 0, 1);
      break;
    default:
      startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
  }

  return { startDate, endDate };
};

const RushdAnalytics: React.FC = () => {
  const [membershipMetrics, setMembershipMetrics] = useState<MembershipMetrics | null>(null);
  const [usageMetrics, setUsageMetrics] = useState<UsageMetrics | null>(null);
  const [financialMetrics, setFinancialMetrics] = useState<FinancialMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<string>('30days');
  const [lastUpdated, setLastUpdated] = useState<string>('');



  // Fetch analytics data using direct Firebase service
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Clear cache before fetching new period data
      clearAnalyticsCache();
      
      const { startDate, endDate } = getPeriodDateRange(period);
      console.log(`🔄 Fetching Rush'd analytics for period: ${period}`, { 
        startDate: startDate.toISOString(), 
        endDate: endDate.toISOString() 
      });

      // Reset all metrics to null while loading
      setMembershipMetrics(null);
      setUsageMetrics(null);
      setFinancialMetrics(null);

      // Fetch all analytics data using the new service (force fresh data)
      const [membershipData, usageData, financialData] = await Promise.all([
        calculateMembershipMetrics(startDate, endDate),
        calculateUsageMetrics(startDate, endDate),
        calculateFinancialMetrics(startDate, endDate),
      ]);

      console.log('✅ Analytics data loaded for period:', period, { 
        membershipData: {
          totalMembers: membershipData.totalMembers,
          activeMembers: membershipData.activeMembers,
          newMembers: membershipData.newMembersThisPeriod
        },
        usageData: {
          totalShipments: usageData.totalShipments,
          expressShipments: usageData.expressShipmentsUsed,
          savings: usageData.savingsGenerated
        },
        financialData: {
          totalRevenue: financialData.totalRevenue,
          mrr: financialData.monthlyRecurringRevenue
        }
      });

      // Set all metrics at once to trigger re-render
      setMembershipMetrics(membershipData);
      setUsageMetrics(usageData);
      setFinancialMetrics(financialData);
      setLastUpdated(new Date().toLocaleTimeString());

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load analytics data';
      setError(errorMessage);
      toast.error(`Analytics Error: ${errorMessage}`);
      console.error('Rush\'d Analytics Error:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  // Export analytics data using direct Firebase service
  const exportData = async (format: 'csv' | 'json') => {
    try {
      const { startDate, endDate } = getPeriodDateRange(period);
      
      const exportResult = await exportAnalyticsData(startDate, endDate, format);
      
      // Create and trigger download
      const blob = new Blob([exportResult.data], { type: exportResult.contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = exportResult.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Analytics data exported as ${format.toUpperCase()}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export analytics data';
      toast.error(`Export Error: ${errorMessage}`);
      console.error('Export Error:', err);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  // Refresh data function that clears cache
  const refreshAnalytics = async () => {
    console.log('🔄 Manual refresh triggered');
    clearAnalyticsCache();
    await fetchAnalytics();
  };

  // Load data on component mount and when date range changes
  useEffect(() => {
    console.log('📊 Period changed, fetching analytics:', period, 'at', new Date().toLocaleTimeString());
    fetchAnalytics();
  }, [period, fetchAnalytics]);

  return (
    <div className="space-y-6">
      {/* Period Selection and Actions */}
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Time Period:</label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Last 7 days</SelectItem>
              <SelectItem value="30days">Last 30 days</SelectItem>
              <SelectItem value="90days">Last 90 days</SelectItem>
              <SelectItem value="current_month">Current month</SelectItem>
              <SelectItem value="last_month">Last month</SelectItem>
              <SelectItem value="current_year">Current year</SelectItem>
            </SelectContent>
          </Select>
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">
              Updated: {lastUpdated}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => exportData('csv')}
            variant="outline"
            size="sm"
            disabled={loading || !membershipMetrics}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => exportData('json')}
            variant="outline"
            size="sm"
            disabled={loading || !membershipMetrics}
          >
            <Download className="h-4 w-4 mr-2" />
            Export JSON
          </Button>
          <Button
            onClick={refreshAnalytics}
            variant="outline"
            size="sm"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Analytics Content */}
      {!loading && membershipMetrics && usageMetrics && financialMetrics && (
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="membership">Membership</TabsTrigger>
            <TabsTrigger value="usage">Usage</TabsTrigger>
            <TabsTrigger value="financial">Financial</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Members</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{membershipMetrics.totalMembers}</div>
                  <p className="text-xs text-muted-foreground">
                    +{membershipMetrics.newMembersThisPeriod} this period
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(financialMetrics.monthlyRecurringRevenue)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {financialMetrics.revenueGrowth > 0 ? '+' : ''}
                    {formatPercentage(financialMetrics.revenueGrowth)} from last period
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Express Shipments</CardTitle>
                  <Package className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{usageMetrics.expressShipmentsUsed}</div>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(usageMetrics.savingsGenerated)} in member savings
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                  <Target className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatPercentage(membershipMetrics.conversionRate)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Visitor to member conversion
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Insights */}
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Performance Highlights
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Active Members</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{membershipMetrics.activeMembers}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatPercentage(membershipMetrics.activeMembers / membershipMetrics.totalMembers)}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Churn Rate</span>
                    <Badge variant={membershipMetrics.churnRate > 0.1 ? "destructive" : "default"}>
                      {formatPercentage(membershipMetrics.churnRate)}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Avg. Lifetime Value</span>
                    <span className="font-medium">
                      {formatCurrency(membershipMetrics.averageLifetimeValue)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Avg. Revenue Per User</span>
                    <span className="font-medium">
                      {formatCurrency(financialMetrics.averageRevenuePerUser)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Usage Patterns
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Usage by Plan</div>
                    {usageMetrics.usageByPlan.map((plan) => (
                      <div key={plan.plan} className="flex justify-between items-center">
                        <span className="text-sm capitalize">{plan.plan}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-muted rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full"
                              style={{ width: `${plan.percentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground w-8">
                            {plan.count}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Average Order Value</span>
                      <span className="font-medium">
                        {formatCurrency(usageMetrics.averageOrderValue)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Membership Tab */}
          <TabsContent value="membership" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle>Membership Growth</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Total Members</span>
                      <span className="font-bold">{membershipMetrics.totalMembers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Active Members</span>
                      <span className="font-bold text-green-600">{membershipMetrics.activeMembers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">New This Period</span>
                      <span className="font-bold text-blue-600">+{membershipMetrics.newMembersThisPeriod}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Retention Metrics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Churn Rate</span>
                      <Badge variant={membershipMetrics.churnRate > 0.1 ? "destructive" : "default"}>
                        {formatPercentage(membershipMetrics.churnRate)}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Conversion Rate</span>
                      <span className="font-bold">{formatPercentage(membershipMetrics.conversionRate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Lifetime Value</span>
                      <span className="font-bold">{formatCurrency(membershipMetrics.averageLifetimeValue)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Revenue Split</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Monthly Plans</span>
                      <span className="font-bold">{formatCurrency(membershipMetrics.monthlyRevenue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Yearly Plans</span>
                      <span className="font-bold">{formatCurrency(membershipMetrics.yearlyRevenue)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-sm font-medium">Total Revenue</span>
                      <span className="font-bold">{formatCurrency(membershipMetrics.monthlyRevenue + membershipMetrics.yearlyRevenue)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Usage Tab */}
          <TabsContent value="usage" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Shipping Usage</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Total Shipments</span>
                      <span className="font-bold">{usageMetrics.totalShipments}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Express Used</span>
                      <span className="font-bold text-blue-600">{usageMetrics.expressShipmentsUsed}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Express Usage Rate</span>
                      <span className="font-bold">
                        {formatPercentage(usageMetrics.expressShipmentsUsed / usageMetrics.totalShipments)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-sm">Savings Generated</span>
                      <span className="font-bold text-green-600">
                        {formatCurrency(usageMetrics.savingsGenerated)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Usage by Plan</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {usageMetrics.usageByPlan.map((plan) => (
                    <div key={plan.plan} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium capitalize">{plan.plan} Plan</span>
                        <span className="text-sm text-muted-foreground">
                          {plan.count} shipments ({formatPercentage(plan.percentage / 100)})
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full"
                          style={{ width: `${plan.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle>Revenue Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Total Revenue</span>
                      <span className="font-bold">{formatCurrency(financialMetrics.totalRevenue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Refunds</span>
                      <span className="font-bold text-red-600">-{formatCurrency(financialMetrics.refunds)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-sm font-medium">Net Revenue</span>
                      <span className="font-bold">{formatCurrency(financialMetrics.netRevenue)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recurring Revenue</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Monthly Recurring</span>
                      <span className="font-bold">{formatCurrency(financialMetrics.monthlyRecurringRevenue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Yearly Recurring</span>
                      <span className="font-bold">{formatCurrency(financialMetrics.yearlyRecurringRevenue)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-sm font-medium">Growth Rate</span>
                      <Badge variant={financialMetrics.revenueGrowth > 0 ? "default" : "destructive"}>
                        {financialMetrics.revenueGrowth > 0 ? '+' : ''}{formatPercentage(financialMetrics.revenueGrowth)}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Per User Metrics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Avg Revenue Per User</span>
                      <span className="font-bold">{formatCurrency(financialMetrics.averageRevenuePerUser)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Lifetime Value</span>
                      <span className="font-bold">{formatCurrency(membershipMetrics.averageLifetimeValue)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default RushdAnalytics;